// frontend/customer-portal/src/components/PlanChangeConfirmModal.jsx
import React from 'react';
import { X, ArrowRight } from 'lucide-react';
import { useDateFormat } from '../hooks/useDateFormat';

export default function PlanChangeConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  fromPlanName,
  fromPlanPrice,
  toPlanName,
  toPlanPrice,
  billingPeriod,
  proratedAmount,
  nextBillingDate,
  paymentMethodBrand,
  paymentMethodLast4,
  isLoading,
  isSubmitting,
}) {
  // nextBillingDate is java.time.LocalDate on the backend -> date-only, no tz shift.
  // Hook must run before the early return.
  const { formatDateOnly } = useDateFormat();

  if (!isOpen) return null;

  const formattedProrated = proratedAmount != null
    ? `$${Number(proratedAmount).toFixed(2)}`
    : '$0.00';

  const formattedNextDate = nextBillingDate
    ? formatDateOnly(nextBillingDate)
    : '—';

  const paymentText = (paymentMethodBrand && paymentMethodLast4)
    ? `${paymentMethodBrand.charAt(0).toUpperCase() + paymentMethodBrand.slice(1)} •••• ${paymentMethodLast4}`
    : 'your payment method on file';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Confirm Plan Change</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {isLoading ? (
            /* Loading skeleton */
            <div className="space-y-4 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-3/4" />
              <div className="h-16 bg-gray-100 rounded" />
              <div className="h-12 bg-gray-100 rounded" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ) : (
            <>
              {/* Plan change summary */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1 text-center">
                  <p className="text-xs text-gray-500 mb-1">Current Plan</p>
                  <p className="font-semibold text-gray-900">{fromPlanName}</p>
                  <p className="text-sm text-gray-600">${fromPlanPrice}/month</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 text-center">
                  <p className="text-xs text-gray-500 mb-1">New Plan</p>
                  <p className="font-semibold text-blue-700">{toPlanName}</p>
                  <p className="text-sm text-blue-600">${toPlanPrice}/month</p>
                </div>
              </div>

              {/* Prorated charge notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  A prorated charge of{' '}
                  <strong>{formattedProrated}</strong>{' '}
                  will be applied to{' '}
                  <strong>{paymentText}</strong>{' '}
                  today.
                </p>
              </div>

              {/* Next billing date */}
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Next regular billing date</span>
                <span className="font-medium text-gray-900">{formattedNextDate}</span>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading || isSubmitting}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Processing...' : `Confirm & Pay ${formattedProrated}`}
          </button>
        </div>
      </div>
    </div>
  );
}
